import { useMemo, useRef, useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { SmsThreadMessage } from '@numeo/types'
import { attachmentKindOf, type AttachmentKind } from '@numeo/types-core'
import { FILE_KIND_ICON, FILE_KIND_LABEL } from '@/utils/fileKind'
import { log } from '@/utils/logger'
import { trackBoxTruckSmsAttachmentFailed } from '../utils/analytics'

/**
 * What a driver attached, shown rather than named.
 *
 * It used to be a link whose text was the MIME type, so a photo of a damaged pallet read
 * `image/jpeg` and a signed rate confirmation read
 * `application/vnd.openxmlformats-officedocument.wordprocessingml.document` — a dispatcher had to
 * open each one in a new tab to find out which was which. Nearly nine in ten attachments on this
 * line are photos, so they are the ones worth rendering in place.
 *
 * Everything points straight at the vendor's URL: those are public and cached for a month, so the
 * browser fetches them directly and nothing passes through Numeo. Nothing new is stored either —
 * `media` has been on the row since the webhook first wrote it.
 *
 * A preview here is a PREVIEW: small enough that a photo cannot push the conversation off the
 * screen, and every one of them opens full size in a new tab, which is where an operator can zoom,
 * rotate, save or print it with the tools they already know.
 */
type Attachment = SmsThreadMessage['media'][number]

/** The glyph's tint inside a chip whose background already says who sent the message. Only the
 *  colour is local: which icon and what to call it come from the shared classifier. */
const TINT_BY_KIND: Record<AttachmentKind, string> = {
    pdf: 'text-danger',
    spreadsheet: 'text-success',
    presentation: 'text-warning',
    word: 'text-accent',
    audio: 'text-accent',
    video: 'text-accent',
    image: 'text-foreground-muted',
    text: 'text-foreground-muted',
    other: 'text-foreground-muted',
}

const FileChip = ({ attachment, kind, outbound }: { attachment: Attachment; kind: AttachmentKind; outbound: boolean }) => {
    const { [kind]: Icon } = FILE_KIND_ICON
    const label = FILE_KIND_LABEL[kind]
    const shell = outbound ? 'border-white/25 bg-white/10 hover:bg-white/20' : 'border-border bg-surface hover:bg-default'
    return (
        <a
            href={attachment.url}
            target="_blank"
            rel="noreferrer"
            title={`Open ${label.toLowerCase()} in a new tab`}
            className={`mt-1 flex w-full max-w-[260px] items-center gap-1.5 rounded-lg border px-2 py-1.5 text-xs font-medium ${shell}`}
        >
            <Icon size={15} className={outbound ? 'text-white/90' : TINT_BY_KIND[kind]} />
            <span className="truncate">{label}</span>
            <ExternalLink size={12} className="ml-auto shrink-0 opacity-70" />
        </a>
    )
}

export const SmsAttachments = ({ media, outbound }: { media: Attachment[]; outbound: boolean }) => {
    /** Images the browser could not decode, or whose link has stopped resolving. They fall back to a
     *  chip rather than leaving a broken frame in the thread — the vendor's URLs are signed, and
     *  nobody can promise today what a link will do in a year. */
    const [broken, setBroken] = useState<Record<string, true>>({})
    /** Reported once per URL: `onError` can fire again on re-render, and a failure counted twice is
     *  the same as not counting it. */
    const reported = useRef<Set<string>>(new Set())

    /**
     * A failure worth recording, not just worth hiding.
     *
     * Whether these links expire is the open question behind this whole feature — the fallback keeps
     * the thread usable, and the event is what would show the problem growing with message age, so
     * the proxy-or-copy decision has numbers rather than opinions. The MIME type is reported; the
     * signed URL is not.
     */
    const markBroken = (attachment: Attachment, kind: 'image' | 'audio') => {
        if (!reported.current.has(attachment.url)) {
            reported.current.add(attachment.url)
            trackBoxTruckSmsAttachmentFailed(kind, attachment.type)
            log.w('[box-truck] an SMS attachment did not load', { kind, type: attachment.type })
        }
        setBroken((prev) => (prev[attachment.url] ? prev : { ...prev, [attachment.url]: true }))
    }
    /**
     * Classified once, by the shared classifier, and then every decision reads that.
     *
     * It was asked for the chip's icon and then asked AGAIN, differently, to decide what to render:
     * a raw `type.startsWith('image/')`. The two disagreed — an attachment whose type is null but
     * whose URL ends `.jpg` is a photo to one and a file to the other — and the kind that decides
     * what an operator sees should not be a second opinion.
     */
    const classified = useMemo(() => media.map((m) => ({ ...m, kind: attachmentKindOf({ mimeType: m.type, url: m.url }) })), [media])
    if (!classified.length) return null

    const photos = classified.filter((m) => m.kind === 'image' && !broken[m.url])
    const rest = classified.filter((m) => m.kind !== 'image' || broken[m.url])

    return (
        <div className="mt-1.5">
            {/* Capped on purpose: a phone photo is 3 000 px wide, and at full width one of them is the
                whole panel. Small enough to read the thread around it, large enough to tell a BOL from
                a damaged pallet — the tab behind the click is where it gets looked at properly. */}
            {photos.length > 0 && (
                <div className={`grid max-w-[260px] gap-1 ${photos.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {photos.map((photo) => (
                        <a
                            key={photo.url}
                            href={photo.url}
                            target="_blank"
                            rel="noreferrer"
                            title="Open photo in a new tab"
                            className="block overflow-hidden rounded-lg border border-black/10 bg-black/5 transition-opacity hover:opacity-90"
                        >
                            <img
                                src={photo.url}
                                alt="Attachment"
                                loading="lazy"
                                onError={() => markBroken(photo, 'image')}
                                className={`w-full object-cover ${photos.length > 1 ? 'h-20' : 'h-32'}`}
                            />
                        </a>
                    ))}
                </div>
            )}
            {rest.map((attachment) =>
                attachment.kind === 'audio' && !broken[attachment.url] ? (
                    // Playable where it sits: a voice note is answered by listening, not by leaving
                    // the thread for a browser tab. `preload="none"` so a thread of them costs nothing
                    // until one is played.
                    <audio
                        key={attachment.url}
                        controls
                        preload="none"
                        src={attachment.url}
                        onError={() => markBroken(attachment, 'audio')}
                        className="mt-1 h-9 w-full max-w-[260px]"
                    />
                ) : (
                    <FileChip key={attachment.url} attachment={attachment} kind={attachment.kind} outbound={outbound} />
                )
            )}
        </div>
    )
}
